import { create } from 'zustand';

/**
 * Whether this user has asked for large documents to load a part at a time.
 *
 * One switch for the user, not one per document: a reader who needs it for a 2 MB report needs it
 * for the next one too. Which documents it then applies to is decided by size alone - see
 * `shouldLoadInParts`.
 *
 * In localStorage rather than on the user record, because it describes this browser: a fast desktop
 * and a laptop want different answers, and it needs no backend to ship.
 */
const STORAGE_KEY = 'confideck.largeDocument.fastLoading';

const read = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false; // private mode, or storage disabled
  }
};

type TFastLoadingStore = {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
};

const useFastLoadingStore = create<TFastLoadingStore>((set) => ({
  enabled: read(),
  setEnabled: (enabled) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(enabled));
    } catch {
      // Not being able to remember it is not a reason to refuse the change for this session.
    }
    set({ enabled });
  },
}));

/** Readable outside React - the load path is not a component. */
export const isFastLoadingEnabled = (): boolean => useFastLoadingStore.getState().enabled;

export default useFastLoadingStore;
